import React, { useEffect } from 'react';

interface ModalProps {
  isActive: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  closeOnBackgroundClick?: boolean;
  className?: string;
}

/**
 * A reusable modal component based on Bulma's modal styles
 */
const Modal: React.FC<ModalProps> = ({
  isActive,
  onClose,
  title,
  children,
  footer,
  size = 'medium',
  closeOnBackgroundClick = true,
  className = '',
}) => {
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isActive) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);

    // Prevent body scrolling when modal is open
    if (isActive) {
      document.documentElement.classList.add('is-clipped');
    } else {
      document.documentElement.classList.remove('is-clipped');
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.documentElement.classList.remove('is-clipped');
    };
  }, [isActive, onClose]);

  const handleBackgroundClick = () => {
    if (closeOnBackgroundClick) {
      onClose();
    }
  };

  // If not active, don't render anything
  if (!isActive) return null;

  const sizeClass = size === 'small'
    ? 'modal-content--small'
    : size === 'large'
      ? 'modal-content--large'
      : '';

  return (
    <div className={`modal is-active ${className}`}>
      <div className="modal-background" onClick={handleBackgroundClick}></div>

      {/* If we have a title or footer, use modal-card structure */}
      {(title || footer) ? (
        <div className="modal-card">
          {title && (
            <header className="modal-card-head">
              <p className="modal-card-title">{title}</p>
              <button
                className="delete"
                aria-label="close"
                onClick={onClose}
              ></button>
            </header>
          )}

          <section className="modal-card-body">
            {children}
          </section>

          {footer && (
            <footer className="modal-card-foot">
              {footer}
            </footer>
          )}
        </div>
      ) : (
        // Simple content modal without header/footer
        <div className={`modal-content ${sizeClass}`}>
          <div className="box">
            {children}
            <button
              className="modal-close is-large"
              aria-label="close"
              onClick={onClose}
            ></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Modal;
